import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { OrderStatus } from '@ecom/contracts';
import {
  EMPTY_ORDER_FILTERS,
  useOrdersStore,
  type OrderFilters,
} from '@/stores/orders.store';
import { useUiStore } from '@/i18n';
import { useT, useStatusLabel } from '@/i18n/use-t';
import { age, count, money } from '@/lib/format';
import { itemCount } from './item-count';
import {
  ORDER_STATUSES,
  ORDER_VIEWS,
  bulkMoves,
  isAwaitingFulfilment,
  notifiesCustomer,
  statusTone,
  viewOf,
} from '@/lib/order-status';
import { paymentLabelKey, paymentTone } from '@/lib/payment-state';
import { FilterBar, FilterField, ToggleGroup } from '@/components/filters';
import { ToggleField } from '@/components/fields';
import { ListCard, ListToolbar, LoadMore, Page, Sheet, TableScroll, Table, Tabs, Td, Th, Tr } from '@/components/page';
import {
  Alert,
  Button,
  ButtonLink,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  TableSkeleton,
  Textarea,
} from '@/components/ui';
import { UploadIcon } from '@/components/icons';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { adminApi, errorMessage } from '@/lib/api-client';
import { AsyncSelect } from '@/components/async-select';
import { staffOptions } from '@/lib/options';
import { openFile } from '@/lib/download';

/** The most labels one print run carries, as the API holds it (ADR-068). */
const LABELS_PER_RUN = 50;

/**
 * The orders list — the screen the store is run from.
 *
 * Search and status filtering happen server-side; "load more" walks the cursor.
 * Nothing here filters an in-memory array, because the table outgrows memory
 * on a real store within months.
 */
export function OrdersListPage() {
  const { items, loading, loadingMore, error, nextCursor, filters, total, counts, exporting, updating, load, loadMore, exportCsv, updateMany, assignMany } =
    useOrdersStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const statusLabel = useStatusLabel();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  /**
   * One chip per active filter.
   *
   * Built here rather than inside the bar because only this screen knows how to
   * word "Placed from 1 July" — and a chip nobody can read is no better than a
   * hidden filter.
   */
  const hasFilters =
    Boolean(filters.search) ||
    filters.statuses.length > 0 ||
    Boolean(filters.paymentMethod || filters.from || filters.to || filters.minTotal || filters.maxTotal);

  const chips = [
    ...filters.statuses.map((status) => ({
      key: `status-${status}`,
      label: statusLabel(status),
      onRemove: () => void load({ statuses: filters.statuses.filter((entry) => entry !== status) }),
    })),
    ...(filters.paymentMethod
      ? [
          {
            key: 'payment',
            label: t(`orderMethod${filters.paymentMethod}` as 'orderMethodCOD'),
            onRemove: () => void load({ paymentMethod: '' }),
          },
        ]
      : []),
    ...(filters.from
      ? [{ key: 'from', label: `${t('filterFrom')}: ${filters.from}`, onRemove: () => void load({ from: '' }) }]
      : []),
    ...(filters.to
      ? [{ key: 'to', label: `${t('filterTo')}: ${filters.to}`, onRemove: () => void load({ to: '' }) }]
      : []),
    ...(filters.minTotal
      ? [
          {
            key: 'min',
            label: `${t('filterMinTotal')}: ${filters.minTotal}`,
            onRemove: () => void load({ minTotal: '' }),
          },
        ]
      : []),
    ...(filters.sort === 'oldest'
      ? [{ key: 'sort', label: t('sortOldestFirst'), onRemove: () => void load({ sort: 'newest' }) }]
      : []),
    ...(filters.assignee
      ? [
          {
            key: 'assignee',
            label: filters.assignee === 'me' ? t('assignedToMe') : filters.assignee === 'none' ? t('assignedToNobody') : t('assignedTo'),
            onRemove: () => void load({ assignee: '' }),
          },
        ]
      : []),
    ...(filters.maxTotal
      ? [
          {
            key: 'max',
            label: `${t('filterMaxTotal')}: ${filters.maxTotal}`,
            onRemove: () => void load({ maxTotal: '' }),
          },
        ]
      : []),
  ];

  // A view named in the address — the bell's "orders to fulfil" — opens on
  // that view once; after that the tabs are the operator's, and the address
  // lets go of it so a reload does not undo their choice.
  // A search in the address does the same — a customer's page opening "all
  // their orders" by their email.
  const [params, setParams] = useSearchParams();
  const linked = useRef({
    view: ORDER_VIEWS.find((view) => view.id === params.get('view')) ?? null,
    search: params.get('search'),
  });

  // Re-read on a language change: item names come localized from the API.
  useEffect(() => {
    const { view, search } = linked.current;
    if (view || search !== null) {
      linked.current = { view: null, search: null };
      setParams({}, { replace: true });
      void load({
        ...EMPTY_ORDER_FILTERS,
        ...(view ? { statuses: [...view.statuses] } : {}),
        ...(search !== null ? { search } : {}),
      });
    } else {
      void load();
    }
  }, [load, locale, setParams]);

  const waiting = items.filter((order) => isAwaitingFulfilment(order.status)).length;

  // ─── Selection (ADR-059) ───────────────────────────────────────────────────
  // Held here, not in the store: it is what this screen is pointing at, and a
  // new filter or page of results starts it again.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState<OrderStatus | null>(null);
  const [bulkNote, setBulkNote] = useState('');
  const [bulkNotify, setBulkNotify] = useState(true);
  const [labelError, setLabelError] = useState<string | null>(null);
  // Giving the selection to someone (ADR-074): open, and who — '' for nobody.
  const [assigning, setAssigning] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkOutcome, setBulkOutcome] = useState<{ done: number; failed: { number: string; message: string }[] } | null>(null);

  useEffect(() => setSelected(new Set()), [filters]);

  const chosen = useMemo(() => items.filter((order) => selected.has(order.id)), [items, selected]);
  const commonMoves = bulkMoves(chosen);
  const allShown = items.length > 0 && items.every((order) => selected.has(order.id));

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function applyBulk() {
    if (!bulkTarget) return;
    const result = await updateMany([...selected], {
      status: bulkTarget,
      ...(bulkNote.trim() ? { reason: bulkNote.trim() } : {}),
      ...(notifiesCustomer(bulkTarget) ? { notifyCustomer: bulkNotify } : {}),
    });
    setBulkOutcome(result);
    setBulkTarget(null);
    setBulkNote('');
    setBulkNotify(true);
    setSelected(new Set(result.failed.map((failure) => failure.id)));
  }

  async function applyAssign() {
    const result = await assignMany([...selected], bulkAssignee || null);
    setBulkOutcome(result);
    setAssigning(false);
    setBulkAssignee('');
    setSelected(new Set(result.failed.map((failure) => failure.id)));
  }

  /** A view's count is the sum of its statuses'; "all" is every order. */
  const viewCount = (statuses: readonly OrderStatus[]) => {
    if (!counts) return undefined;
    const included = statuses.length === 0 ? ORDER_STATUSES : statuses;
    return count(
      included.reduce((sum, status) => sum + (counts[status] ?? 0), 0),
      locale,
    );
  };

  return (
    <Page
      title={t('orders')}
      subtitle={waiting > 0 ? t('actionQueueHint') : t('ordersSubtitle')}
      actions={
        <>
          <Button variant="secondary" onClick={() => void exportCsv()} disabled={exporting} title={t('exportOrdersHint')}>
            <UploadIcon className="size-4 rotate-180" />
            {exporting ? t('loading') : t('exportOrders')}
          </Button>
          {/* A phone or WhatsApp order, placed on the customer's behalf (ADR-062). */}
          {can(user, 'orders:fulfill') ? <ButtonLink to="/orders/new">{t('newOrder')}</ButtonLink> : null}
        </>
      }
      fill
    >
      {labelError ? <Alert>{labelError}</Alert> : null}
      {bulkOutcome ? (
        <Alert tone={bulkOutcome.failed.length > 0 ? 'error' : 'success'}>
          <span className="block">
            {bulkOutcome.failed.length > 0
              ? t('bulkResult', { done: count(bulkOutcome.done, locale), failed: count(bulkOutcome.failed.length, locale) })
              : bulkOutcome.done === 1
                ? t('bulkDoneOne')
                : t('bulkDone', { done: count(bulkOutcome.done, locale) })}
          </span>
          {bulkOutcome.failed.map((failure) => (
            <span key={failure.number} className="block text-xs">
              <span dir="ltr">{failure.number}</span>: {failure.message}
            </span>
          ))}
        </Alert>
      ) : null}

      <ListCard>
        <Tabs
          className="px-2"
          label={t('orders')}
          value={viewOf(filters.statuses) ?? 'custom'}
          onChange={(id) => void load({ statuses: [...(ORDER_VIEWS.find((view) => view.id === id)?.statuses ?? [])] })}
          items={ORDER_VIEWS.map((view) => ({
            id: view.id,
            label: t(`orderView_${view.id}`),
            count: viewCount(view.statuses),
          }))}
        />
        <ListToolbar>
          <FilterBar
            search={filters.search}
            onSearch={(search) => void load({ search })}
            placeholder={t('searchOrdersHint')}
            total={total ?? undefined}
            shown={items.length}
            chips={chips}
            onClearAll={() => void load(EMPTY_ORDER_FILTERS)}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <FilterField label={t('filterStatus')}>
                <ToggleGroup
                  options={ORDER_STATUSES.map((status) => ({
                    value: status,
                    label: statusLabel(status),
                  }))}
                  selected={filters.statuses}
                  onChange={(statuses) => void load({ statuses: statuses as OrderStatus[] })}
                />
              </FilterField>

              <FilterField label={t('sortOrders')} htmlFor="f-sort">
                <Select
                  id="f-sort"
                  value={filters.sort}
                  onChange={(event) => void load({ sort: event.target.value as OrderFilters['sort'] })}
                >
                  <option value="newest">{t('sortNewestFirst')}</option>
                  <option value="oldest">{t('sortOldestFirst')}</option>
                </Select>
              </FilterField>

              {/* Who has it (ADR-074). */}
              <FilterField label={t('assignedTo')} htmlFor="f-assignee">
                <Select id="f-assignee" value={filters.assignee} onChange={(event) => void load({ assignee: event.target.value })}>
                  <option value="">{t('anyValue')}</option>
                  <option value="me">{t('assignedToMe')}</option>
                  <option value="none">{t('assignedToNobody')}</option>
                </Select>
              </FilterField>

              <FilterField label={t('filterPaymentMethod')} htmlFor="f-payment">
                <Select
                  id="f-payment"
                  value={filters.paymentMethod}
                  onChange={(event) =>
                    void load({ paymentMethod: event.target.value as OrderFilters['paymentMethod'] })
                  }
                >
                  <option value="">{t('anyValue')}</option>
                  <option value="COD">{t('orderMethodCOD')}</option>
                  <option value="CARD">{t('orderMethodCARD')}</option>
                  <option value="MANUAL">{t('orderMethodMANUAL')}</option>
                </Select>
              </FilterField>

              <div className="grid grid-cols-2 gap-2">
                <FilterField label={t('filterFrom')} htmlFor="f-from">
                  <Input
                    id="f-from"
                    type="date"
                    dir="ltr"
                    value={filters.from}
                    onChange={(event) => void load({ from: event.target.value })}
                  />
                </FilterField>
                <FilterField label={t('filterTo')} htmlFor="f-to">
                  <Input
                    id="f-to"
                    type="date"
                    dir="ltr"
                    value={filters.to}
                    onChange={(event) => void load({ to: event.target.value })}
                  />
                </FilterField>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <FilterField label={t('filterMinTotal')} htmlFor="f-min">
                  <Input
                    id="f-min"
                    dir="ltr"
                    inputMode="decimal"
                    value={filters.minTotal}
                    onChange={(event) => void load({ minTotal: event.target.value })}
                  />
                </FilterField>
                <FilterField label={t('filterMaxTotal')} htmlFor="f-max">
                  <Input
                    id="f-max"
                    dir="ltr"
                    inputMode="decimal"
                    value={filters.maxTotal}
                    onChange={(event) => void load({ maxTotal: event.target.value })}
                  />
                </FilterField>
              </div>
            </div>
          </FilterBar>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={7} />
        ) : items.length === 0 ? (
          <EmptyState
            title={t('noOrders')}
            body={hasFilters ? t('noOrdersFiltered') : t('noOrdersYet')}
            action={
              hasFilters ? (
                <Button variant="secondary" onClick={() => void load(EMPTY_ORDER_FILTERS)}>
                  {t('clearFilters')}
                </Button>
              ) : null
            }
          />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th className="w-10">
                    <Checkbox
                      label={t('selectAllOrders')}
                      checked={allShown}
                      onChange={() => setSelected(allShown ? new Set() : new Set(items.map((order) => order.id)))}
                    />
                  </Th>
                  <Th>{t('orderNumber')}</Th>
                  <Th>{t('customer')}</Th>
                  <Th align="end">{t('colItems')}</Th>
                  <Th>{t('status')}</Th>
                  <Th>{t('paymentColumn')}</Th>
                  <Th>{t('deliveryColumn')}</Th>
                  <Th align="end">{t('total')}</Th>
                  <Th align="end">{t('placed')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((order) => (
                  // The whole row opens the order; the number stays a real link
                  // for a middle-click or a copied address.
                  <Tr
                    key={order.id}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    selected={selected.has(order.id)}
                  >
                    <Td onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        label={t('selectOrder', { number: order.number })}
                        checked={selected.has(order.id)}
                        onChange={() => toggle(order.id)}
                      />
                    </Td>
                    <Td>
                      <Link
                        to={`/orders/${order.id}`}
                        onClick={(event) => event.stopPropagation()}
                        data-numeric
                        dir="ltr"
                        className="whitespace-nowrap font-medium text-brand-700 hover:underline"
                      >
                        {order.number}
                      </Link>
                      {order.assignee ? <span className="block text-xs text-ink-muted">{order.assignee.name}</span> : null}
                    </Td>
                    <Td>
                      <span className="block text-ink">{order.shippingAddress.fullName}</span>
                      <span className="block text-xs text-ink-muted">{order.email}</span>
                    </Td>
                    <Td align="end" data-numeric className="text-ink-muted">
                      {count(order.items.reduce((sum, item) => sum + item.quantity, 0), locale)}
                    </Td>
                    <Td>
                      <Pill tone={statusTone(order.status)}>{statusLabel(order.status, order.isPickup)}</Pill>
                    </Td>
                    <Td className="whitespace-nowrap">
                      <span className="block text-ink-muted">{t(`orderMethod${order.paymentMethod}` as 'orderMethodCOD')}</span>
                      {/* Cash not yet collected reads differently from a card that paid (ADR-061). */}
                      {order.payment ? (
                        <Pill tone={paymentTone(order.payment.status)}>
                          {t(paymentLabelKey(order.payment.status, order.payment.provider === 'cod'))}
                        </Pill>
                      ) : null}
                    </Td>
                    <Td className="text-ink-muted">
                      <span className="block max-w-32 truncate">
                        {order.isPickup ? t('pickupAtStore') : (order.shippingMethodName ?? order.shippingAddress.city)}
                      </span>
                      <span className="block text-xs text-ink-faint">
                        {itemCount(order.items.reduce((sum, item) => sum + item.quantity, 0), t, locale)}
                      </span>
                    </Td>
                    <Td align="end" data-numeric className="font-medium text-ink">
                      {money(order.totals.grandTotal, locale, order.totals.currency)}
                    </Td>
                    <Td align="end" className="text-ink-muted">
                      {age(order.placedAt ?? order.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>

      {selected.size > 0 ? (
        // The selection's own toolbar, floating over the list rather than
        // pushing it down — a row that moves under the pointer gets the next
        // click meant for its neighbour. Inverted, so it reads in either theme.
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
          <div
            role="toolbar"
            aria-label={t('selectedCount', { count: count(selected.size, locale) })}
            className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-full bg-ink px-3 py-2 text-surface shadow-float"
          >
            <span className="px-2 text-sm font-medium">{t('selectedCount', { count: count(selected.size, locale) })}</span>
            <span className="h-4 w-px bg-surface/25" aria-hidden />
            {commonMoves.length > 0 ? (
              commonMoves.map((move) => (
                <BarButton key={move} onClick={() => setBulkTarget(move)} disabled={updating}>
                  {t('markAs')} {statusLabel(move)}
                </BarButton>
              ))
            ) : (
              <span className="px-2 text-xs text-surface/70">{t('bulkNoCommonMove')}</span>
            )}
            <BarButton onClick={() => navigate(`/orders/print?ids=${[...selected].join(',')}`)}>
              {t('printPackingSlip')}
            </BarButton>
            {/* One 4×6 label a parcel; the API names any order with none to print (ADR-068). */}
            {can(user, 'orders:fulfill') ? (
              <BarButton
                onClick={() => {
                  setLabelError(null);
                  openFile(() => adminApi.orders.labels([...selected]), 'labels.pdf').catch((error: unknown) =>
                    setLabelError(errorMessage(error, 'Could not print the labels.')),
                  );
                }}
                disabled={selected.size > LABELS_PER_RUN}
                title={selected.size > LABELS_PER_RUN ? t('labelsAtMost', { count: count(LABELS_PER_RUN, locale) }) : undefined}
              >
                {t('printShippingLabels')}
              </BarButton>
            ) : null}
            {can(user, 'orders:fulfill') ? (
              <BarButton onClick={() => setAssigning(true)} disabled={updating}>
                {t('assignSelected')}
              </BarButton>
            ) : null}
            <BarButton onClick={() => setSelected(new Set())}>{t('clearSelection')}</BarButton>
          </div>
        </div>
      ) : null}

      {/* Give the selection to someone, or to nobody (ADR-074), one order at a time. */}
      <Sheet
        open={assigning}
        title={t('assignSelectedTitle', { count: count(selected.size, locale) })}
        subtitle={t('assignSelectedHint')}
        onClose={() => setAssigning(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAssigning(false)} disabled={updating}>
              {t('cancel')}
            </Button>
            <Button onClick={() => void applyAssign()} disabled={updating}>
              {updating ? t('saving') : t('save')}
            </Button>
          </>
        }
      >
        <Field label={t('assignedTo')} htmlFor="bulk-assignee">
          <AsyncSelect
            id="bulk-assignee"
            value={bulkAssignee}
            emptyLabel={t('assignedToNobody')}
            fetchPage={staffOptions.page}
            fetchOne={staffOptions.one}
            onChange={setBulkAssignee}
          />
        </Field>
      </Sheet>

      <Sheet
        open={bulkTarget !== null}
        title={
          bulkTarget
            ? selected.size === 1
              ? t('bulkMoveTitleOne', { status: statusLabel(bulkTarget) })
              : t('bulkMoveTitle', { count: count(selected.size, locale), status: statusLabel(bulkTarget) })
            : ''
        }
        subtitle={t('bulkMoveBody')}
        onClose={() => setBulkTarget(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBulkTarget(null)} disabled={updating}>
              {t('cancel')}
            </Button>
            <Button onClick={() => void applyBulk()} disabled={updating}>
              {updating ? t('saving') : t('saveStatus')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label={t('statusNote')} htmlFor="bulk-note" hint={t('statusNoteHint')}>
            <Textarea id="bulk-note" rows={3} maxLength={500} value={bulkNote} onChange={(event) => setBulkNote(event.target.value)} />
          </Field>
          {bulkTarget && notifiesCustomer(bulkTarget) ? (
            <ToggleField label={t('notifyCustomer')} hint={t('notifyCustomerHint')} checked={bulkNotify} onChange={setBulkNotify} />
          ) : (
            <p className="text-xs text-ink-muted">{t('noCustomerMessage')}</p>
          )}
        </div>
      </Sheet>
    </Page>
  );
}

/** A button on the floating selection bar — light on the inverted bar, in either theme. */
function BarButton({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="min-h-8 rounded-full px-3 text-xs font-medium text-surface transition-colors hover:bg-surface/15 disabled:opacity-50"
    >
      {children}
    </button>
  );
}
